/**
 * Generates bank-specific valuation form templates from the base Finance Ireland form.
 * Instead of duplicating 3,500-line form files for each bank, this factory deep-clones
 * the base template and applies targeted patches: disclaimer text, field labels, and
 * legal entity references.
 */

import { fake_valuation_form_response } from "./ireland_valuation_form";

/**
 * Creates a bank-specific form by cloning the base template and applying overrides.
 * @param {object} bankConfig - Bank config from bankConfig.js
 * @returns {object} A form response object in the same shape as fake_valuation_form_response
 */
export function createBankForm(bankConfig) {
  const { legalEntityName, mortgageRefLabel, shortName } = bankConfig;

  // Deep clone the base template
  const form = JSON.parse(JSON.stringify(fake_valuation_form_response));
  const panels = form.data.valuationForm.panels;

  for (const panel of panels) {
    // Patch "Notes and recommendations" — replace generic "the Lender" with bank name
    if (panel.name === "Notes and recommendations") {
      for (const field of panel.fields) {
        if (field.html) {
          field.html = field.html
            .replace(/the Lender's Valuer/g, `the ${shortName} Valuer`)
            .replace(/the Lender/g, legalEntityName);
        }
      }
    }

    // Patch "Legal Disclaimer" — replace Finance Ireland references
    if (panel.name === "Legal Disclaimer - Strictly Private & Confidential") {
      for (const field of panel.fields) {
        if (field.html) {
          field.html = field.html.replace(
            /Finance Ireland Residential Mortgages/g,
            legalEntityName
          );
        }
      }
    }

    // Patch "Summary" — update mortgage reference label
    if (panel.name === "Summary") {
      for (const field of panel.fields) {
        if (field.fieldUI === "mortgage-ref-number" && mortgageRefLabel) {
          field.label = mortgageRefLabel;
        }
      }
    }

    // Patch any TEXT fields in other panels that reference Finance Ireland
    for (const field of panel.fields) {
      if (field.html && panel.name !== "Notes and recommendations") {
        field.html = field.html.replace(
          /Finance Ireland/g,
          legalEntityName
        );
      }
    }
  }

  return form;
}
